import React from 'react';
import { format } from 'date-fns';
// The transparent crest master, as on the App Lock. Imported so the build
// content-hashes it.
import crest from '../assets/crest.webp';

/**
 * The Arcanum membership card, in the 2.0 identity: obsidian and patinated
 * gold with the crest. Colours are fixed, like a real card, so it reads the
 * same wherever it is shown.
 *
 * Same fields as before and nothing more: the name the member chose to show,
 * their number (or standing, for staff), and when they were admitted. No
 * e-mail, no code, nothing that would work as a credential.
 */
export function MemberCardFace({ user }) {
  // Staff don't carry a member number — they get their standing instead, which
  // reads better on the card than a blank or a zero.
  const isFounder = user?.role === 'superadmin';
  const isOperator = user?.role === 'operator';
  const standingLabel = isFounder || isOperator ? 'Standing' : 'No.';
  const memberNo = isFounder
    ? 'Founder'
    : isOperator
      ? 'Operator'
      : (user?.member_no != null ? String(user.member_no).padStart(3, '0') : '—');
  let admitted = '';
  const src = user?.admitted_at || user?.created_at;
  if (src) {
    try { admitted = format(new Date(src), 'MMMM yyyy'); } catch (e) { admitted = ''; }
  }

  const label = { color: '#B5B0A5' };
  const value = { color: '#E8D2A6' };

  return (
    <div
      className="relative rounded-2xl p-5 overflow-hidden"
      style={{
        background: 'linear-gradient(150deg, #16191A 0%, #0E1011 55%, #07090A 100%)',
        border: '1px solid rgba(211,175,104,0.75)',
        boxShadow: '0 12px 32px rgba(0,0,0,0.55)',
        color: '#EFEADE',
      }}
      data-testid="member-card-face"
    >
      {/* A single diagonal sheen, as on the reference card. Very faint. */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{ background: 'linear-gradient(115deg, transparent 45%, rgba(211,175,104,0.06) 55%, transparent 65%)' }}
      />
      {/* gold hairline frame */}
      <div
        className="absolute inset-2 rounded-xl pointer-events-none"
        style={{ border: '1px solid rgba(211,175,104,0.25)' }}
        aria-hidden="true"
      />

      <div className="relative text-center mb-4">
        <p className="font-heading text-lg tracking-[0.24em]" style={value}>ARCANUM</p>
        <p className="text-[9px] uppercase tracking-[0.22em]" style={label}>Membership credential</p>
        <div className="mx-auto mt-2 h-px w-3/4" style={{ background: 'rgba(211,175,104,0.35)' }} aria-hidden="true" />
      </div>

      <div className="relative flex items-center gap-4">
        <img src={crest} alt="" aria-hidden="true" className="w-[34%] max-w-[120px] h-auto shrink-0 select-none" draggable={false} />
        <dl className="min-w-0 flex-1 space-y-2.5">
          <div>
            <dt className="text-[9px] uppercase tracking-[0.2em]" style={label}>Member</dt>
            <dd
              className="font-heading text-lg leading-tight"
              style={{ ...value, overflowWrap: 'anywhere' }}
              data-testid="member-card-name"
            >
              {user?.display_name || user?.name || 'Member'}
            </dd>
          </div>
          <div>
            <dt className="text-[9px] uppercase tracking-[0.2em]" style={label}>{standingLabel}</dt>
            <dd className="font-heading text-lg tabular-nums leading-tight" style={value}>{memberNo}</dd>
          </div>
          <div>
            <dt className="text-[9px] uppercase tracking-[0.2em]" style={label}>Admitted</dt>
            <dd className="text-sm" style={value}>{admitted || '—'}</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}
